import React, { useState, useEffect, createContext, useContext } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { getFirestore, doc, setDoc, onSnapshot, updateDoc, arrayUnion, arrayRemove, getDoc, deleteField, collection, addDoc, deleteDoc } from 'firebase/firestore';
import { Edit2, Trash2, Plus, MapPin, Phone, Mail, Lock, Unlock, Menu, X, Star, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';

import { defaultNavData, defaultHeroData, defaultAboutData, defaultResortData, defaultGalleryData, defaultFooterData } from './defaults';

const SectionReveal = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{ duration: 0.8, delay, ease: "easeOut" }}
  >
    {children}
  </motion.div>
);

/* --- FIREBASE CONFIGURATION (SECURE) --- */
const firebaseConfig = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
  measurementId: "G-7D3LX8G2ZG"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

const APP_ID = 'greenwood-resort-v1';



/* --- EDITABLE COMPONENTS --- */

const EditableText = ({ text, id, onSave, isAdmin, className, tag = "div", ...props }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(text || "");

  useEffect(() => { setValue(text || ""); }, [text]);

  const handleBlur = () => {
    setIsEditing(false);
    if (value !== text) onSave(id, value);
  };

  if (!isAdmin) {
    const Tag = tag;
    return <Tag className={className} {...props}>{text}</Tag>;
  }

  if (isEditing) {
    return (
      <textarea
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={handleBlur}
        className={`w-full bg-black/50 text-white p-2 rounded-lg outline-none border border-white/30 backdrop-blur-md ${className}`}
        style={{ minHeight: '1.5em', resize: 'vertical', overflow: 'auto' }}
      />
    );
  }

  const Tag = tag;
  return (
    <Tag
      onClick={() => setIsEditing(true)}
      {...props}
      className={`${className} cursor-pointer hover:bg-white/10 transition-colors border border-transparent hover:border-white/20 rounded px-1 -mx-1 relative group`}
      title="Click to edit"
    >
      {text || "..."}
      <span className="absolute -top-3 right-0 text-[10px] bg-white text-black px-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none font-sans">Edit</span>
    </Tag>
  );
};

const EditableImage = ({ src, onEditRequest, isAdmin, className, alt, cover = true }) => {
  return (
    <div className={`relative group overflow-hidden ${className}`}>
      <img
        src={src || "https://via.placeholder.com/400"}
        alt={alt}
        className={`w-full h-full ${cover ? 'object-cover' : 'object-contain'} transition-transform duration-700 group-hover:scale-110`}
        onError={(e) => e.target.src = "https://via.placeholder.com/400?text=Image+Error"}
      />

      {isAdmin && (
        <button
          onClick={(e) => { e.stopPropagation(); onEditRequest(); }}
          className="absolute top-3 right-3 bg-black/50 hover:bg-black/80 text-white p-2 rounded-full backdrop-blur-md border border-white/20 shadow-lg opacity-0 group-hover:opacity-100 transition-all z-10"
          title="Change Image"
        >
          <Edit2 size={16} />
        </button>
      )}
    </div>
  );
};

/* --- MODALS --- */

const convertGoogleDriveUrl = (url) => {
  try {
    if (!url) return url;
    // Only process google drive links
    if (url.includes('drive.google.com') || url.includes('docs.google.com')) {
      // Try to match specific patterns first
      const match = url.match(/\/d\/([-\w]+)/) || url.match(/id=([-\w]+)/);
      if (match && match[1]) {
        // Use thumbnail endpoint with large size which is often more reliable than export=view
        return `https://drive.google.com/thumbnail?id=${match[1]}&sz=w1920`;
      }

      // Fallback to looking for a long ID if specific patterns fail
      const idMatch = url.match(/[-\w]{25,}/);
      if (idMatch) {
        return `https://drive.google.com/thumbnail?id=${idMatch[0]}&sz=w1920`;
      }
    }
    return url;
  } catch (e) { return url; }
};

const InputModal = ({ isOpen, onClose, title, defaultValue, onConfirm }) => {
  const [val, setVal] = useState("");

  useEffect(() => { if (isOpen) setVal(defaultValue || ""); }, [isOpen, defaultValue]);
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-stone-900/90 border border-white/10 rounded-3xl p-6 w-full max-w-md shadow-2xl text-white">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-light">{title}</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onConfirm(convertGoogleDriveUrl(val)); onClose(); }} className="space-y-6">
          <div>
            <label className="block text-xs font-bold text-white/50 uppercase tracking-widest mb-2">Image URL</label>
            <input
              type="text"
              value={val}
              onChange={(e) => setVal(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-sm focus:outline-none focus:border-white/30 text-white placeholder-white/20"
              autoFocus
              placeholder="https://..."
            />
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 py-3 text-white/60 hover:bg-white/5 rounded-xl transition-colors">Cancel</button>
            <button type="submit" className="flex-1 bg-white text-black py-3 rounded-xl hover:bg-gray-200 font-medium transition-colors">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ConfirmModal = ({ isOpen, onClose, title, message, onConfirm }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-stone-900/90 border border-white/10 rounded-3xl p-6 w-full max-w-md shadow-2xl text-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-light">{title}</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>
        <p className="text-white/70 mb-6">{message}</p>
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 py-3 text-white/60 hover:bg-white/5 rounded-xl transition-colors">
            Cancel
          </button>
          <button type="button" onClick={() => { onConfirm(); onClose(); }} className="flex-1 bg-red-600 text-white py-3 rounded-xl hover:bg-red-700 font-medium transition-colors">
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const LoginModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [error, setError] = useState("");

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try { await signInWithEmailAndPassword(auth, email, pass); onClose(); }
    catch (err) { setError("Invalid credentials."); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-stone-900/90 border border-white/10 rounded-3xl p-8 w-full max-w-sm shadow-2xl text-white">
        <div className="flex justify-between items-center mb-8">
          <h3 className="text-xl font-light">Admin Access</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-white placeholder-white/30 focus:outline-none focus:border-white/40" />
          <input type="password" value={pass} onChange={(e) => setPass(e.target.value)} placeholder="Password" className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-white placeholder-white/30 focus:outline-none focus:border-white/40" />
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <button type="submit" className="w-full bg-white text-black py-4 rounded-xl font-medium hover:bg-gray-200 transition-colors mt-2">Sign In</button>
        </form>
      </div>
    </div>
  );
};

/* --- MAIN APP --- */
export default function App() {
  return <AppContent />;
}

function AppContent() {
  const [user, setUser] = useState(null);
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);

  // Modal State
  const [inputModal, setInputModal] = useState({ isOpen: false, title: "", defaultValue: "", onConfirm: null });
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: "", message: "", onConfirm: null });

  const [lightboxImage, setLightboxImage] = useState(null);

  // Section Data States
  const [navData, setNavData] = useState(defaultNavData);
  const [heroData, setHeroData] = useState(defaultHeroData);
  const [aboutData, setAboutData] = useState(defaultAboutData);
  const [resortData, setResortData] = useState(defaultResortData);
  const [galleryData, setGalleryData] = useState(defaultGalleryData);
  const [footerData, setFooterData] = useState(defaultFooterData);
  const [experiences, setExperiences] = useState([]);

  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    document.title = "Greenwood Island Resort";
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setIsAdminMode(!!u && !u.isAnonymous);
      if (!u) signInAnonymously(auth).catch(console.error);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;

    const subscribeToSection = (sectionName, defaultData, setter) => {
      const docRef = doc(db, 'resorts', APP_ID, 'sections', sectionName);
      return onSnapshot(docRef, (s) => {
        if (s.exists()) setter(s.data());
        else setDoc(docRef, defaultData).catch(err => console.error(`Error creating ${sectionName} doc:`, err));
      }, (error) => console.error(`${sectionName} snapshot error:`, error));
    };

    const unsubNav = subscribeToSection('navigation', defaultNavData, setNavData);
    const unsubHero = subscribeToSection('hero', defaultHeroData, setHeroData);
    const unsubAbout = subscribeToSection('about_little_andaman', defaultAboutData, setAboutData);
    const unsubResort = subscribeToSection('our_resort', defaultResortData, setResortData);
    const unsubGallery = subscribeToSection('gallery', defaultGalleryData, setGalleryData);
    const unsubFooter = subscribeToSection('contact_us', defaultFooterData, setFooterData);

    // Experiences Subscription
    const expRef = doc(db, 'resorts', APP_ID, 'sections', 'experiences');
    const unsubExp = onSnapshot(expRef, (s) => {
      if (s.exists()) setExperiences(s.data().items || []);
      else setDoc(expRef, { items: [] }).catch(err => console.error('Error creating experiences doc:', err));
      setLoading(false);
    }, (error) => {
      console.error('Experiences snapshot error:', error);
      setLoading(false);
    });

    return () => {
      unsubNav(); unsubHero(); unsubAbout(); unsubResort(); unsubGallery(); unsubFooter(); unsubExp();
    };
  }, [user]);

  // Generic Section Update
  const updateSection = async (sectionName, updates) => {
    if (!user) return;
    try {
      await updateDoc(doc(db, 'resorts', APP_ID, 'sections', sectionName), updates);
      console.log(`✅ ${sectionName} updated`);
    } catch (err) {
      console.error(`❌ Error updating ${sectionName}:`, err);
      alert('Failed to save changes: ' + err.message);
    }
  };

  // Generic Image Array Helpers
  const addImageToSection = (sectionName, currentImages) => {
    setInputModal({
      isOpen: true, title: `Add Image to ${sectionName}`, defaultValue: "",
      onConfirm: (url) => {
        if (url) updateSection(sectionName, { images: arrayUnion(url) });
      }
    });
  };

  const editImageInSection = (sectionName, currentImages, oldUrl, idx) => {
    setInputModal({
      isOpen: true, title: "Replace Image", defaultValue: oldUrl,
      onConfirm: (newUrl) => {
        if (!newUrl || newUrl === oldUrl) return;
        const newImages = [...currentImages];
        newImages[idx] = newUrl;
        updateSection(sectionName, { images: newImages });
      }
    });
  };

  const removeImageFromSection = (sectionName, currentImages, url) => {
    if (currentImages.length <= 1) {
      alert("You must have at least one image in this section.");
      return;
    }
    setConfirmModal({
      isOpen: true, title: "Delete Image", message: "Are you sure you want to remove this image?",
      onConfirm: () => updateSection(sectionName, { images: arrayRemove(url) })
    });
  };

  /* --- EXPERIENCES LOGIC --- */
  const addExperience = async () => {
    const newItem = {
      id: Date.now().toString(),
      title: "New Experience",
      description: "Describe this amazing experience...",
      images: []
    };
    await updateDoc(doc(db, 'resorts', APP_ID, 'sections', 'experiences'), {
      items: arrayUnion(newItem)
    });
  };

  const updateExperience = async (id, updatedItem) => {
    const newItems = experiences.map(item => item.id === id ? updatedItem : item);
    await updateDoc(doc(db, 'resorts', APP_ID, 'sections', 'experiences'), { items: newItems });
  };

  const deleteExperience = (id) => {
    setConfirmModal({
      isOpen: true, title: "Delete Experience", message: "Are you sure you want to remove this experience tile?",
      onConfirm: async () => {
        const newItems = experiences.filter(item => item.id !== id);
        await updateDoc(doc(db, 'resorts', APP_ID, 'sections', 'experiences'), { items: newItems });
      }
    });
  };

  const addExperienceImage = (id) => {
    setInputModal({
      isOpen: true, title: "Add Experience Image", defaultValue: "",
      onConfirm: (url) => {
        if (!url) return;
        const item = experiences.find(i => i.id === id);
        if (item) updateExperience(id, { ...item, images: [...item.images, url] });
      }
    });
  };

  const editExperienceImage = (id, oldUrl, idx) => {
    setInputModal({
      isOpen: true, title: "Replace Image", defaultValue: oldUrl,
      onConfirm: (newUrl) => {
        if (!newUrl || newUrl === oldUrl) return;
        const item = experiences.find(i => i.id === id);
        if (item) {
          const newImages = [...item.images];
          newImages[idx] = newUrl;
          updateExperience(id, { ...item, images: newImages });
        }
      }
    });
  };

  const removeExperienceImage = (id, url) => {
    const item = experiences.find(i => i.id === id);
    if (item) {
      updateExperience(id, { ...item, images: item.images.filter(img => img !== url) });
    }
  };

  const toggleAdmin = async () => {
    if (isAdminMode) { await signOut(auth); setIsAdminMode(false); }
    else setLoginOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-gray-400 font-light tracking-widest animate-pulse">
        LOADING PARADISE...
      </div>
    );
  }

  return (
    <div className="font-sans text-gray-800 bg-white min-h-screen relative selection:bg-blue-200">
      {/* LIGHTBOX */}
      {lightboxImage && (
        <div
          className="fixed inset-0 bg-black/90 z-[101] flex items-center justify-center p-4 backdrop-blur-md animate-in fade-in"
          onClick={() => setLightboxImage(null)}
        >
          <img src={lightboxImage} alt="Lightbox" className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" />
          <button onClick={() => setLightboxImage(null)} className="absolute top-4 right-4 text-white/50 hover:text-white"><X size={24} /></button>
        </div>
      )}

      {/* UI LAYERS */}
      <div className="relative z-10 h-full overflow-y-auto overflow-x-hidden scroll-smooth">

        <ConfirmModal
          isOpen={confirmModal.isOpen}
          title={confirmModal.title}
          message={confirmModal.message}
          onConfirm={confirmModal.onConfirm}
          onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        />

        <LoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} />

        <InputModal
          isOpen={inputModal.isOpen}
          title={inputModal.title}
          defaultValue={inputModal.defaultValue}
          onConfirm={inputModal.onConfirm}
          onClose={() => setInputModal({ ...inputModal, isOpen: false })}
        />

        {/* ADMIN BAR */}
        {isAdminMode && (
          <div className="bg-black/80 backdrop-blur-md text-white/80 px-4 py-2 text-xs flex justify-between items-center sticky top-0 z-50 border-b border-white/10">
            <div className="flex items-center gap-2">
              <Edit2 size={12} />
              <span>Admin Active</span>
            </div>
            <button onClick={toggleAdmin} className="hover:text-white transition-colors">Exit</button>
          </div>
        )}

        {/* NAVBAR */}
        <nav className={`sticky top-0 z-40 px-6 py-4 transition-all duration-300 ${isAdminMode ? 'pt-0' : ''}`}>
          {isAdminMode && (
            <div className="h-[40px]"></div>
          )}

          <motion.div
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="max-w-7xl mx-auto bg-blue-100/60 backdrop-blur-md border border-blue-200/30 rounded-full px-4 md:px-6 py-2 md:py-3 flex justify-between items-center shadow-sm"
          >
            <div className="flex items-center gap-2 text-primary">
              <EditableText
                tag="span"
                className="font-semibold text-xl tracking-tight"
                text={navData.title}
                id="navTitle"
                isAdmin={isAdminMode}
                onSave={(_, val) => updateSection('navigation', { title: val })}
              />
            </div>
            <div className="hidden md:flex items-center gap-8 text-sm font-medium text-secondary/80">
              {navData.links.map((link, i) => (
                <a key={link.id} href={link.href} className="hover:text-primary transition-colors">
                  <EditableText
                    tag="span"
                    text={link.text}
                    id={link.id}
                    isAdmin={isAdminMode}
                    onSave={(_, val) => {
                      const newLinks = [...navData.links];
                      newLinks[i].text = val;
                      updateSection('navigation', { links: newLinks });
                    }}
                  />
                </a>
              ))}
            </div>

            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-primary">
              {mobileMenuOpen ? <X /> : <Menu />}
            </button>
          </motion.div>

          {/* Mobile Dropdown */}
          {mobileMenuOpen && (
            <div className="mt-2 bg-gradient-to-r from-white via-blue-50 to-pink-50 bg-[length:400%_400%] animate-gradient-xy backdrop-blur-xl border border-white/40 rounded-2xl p-6 space-y-4 text-center md:hidden animate-in slide-in-from-top-5 shadow-xl">
              {navData.links.map(link => (
                <a key={link.id} href={link.href} onClick={() => setMobileMenuOpen(false)} className="block py-2 text-secondary hover:text-primary font-medium text-lg">{link.text}</a>
              ))}
            </div>
          )}
        </nav>

        <main>
          {/* HERO SECTION */}
          <HeroCarousel
            data={heroData}
            isAdmin={isAdminMode}
            onSave={(key, val) => updateSection('hero', { [key]: val })}
            onAdd={() => addImageToSection('hero', heroData.images)}
            onEdit={(oldUrl, idx) => editImageInSection('hero', heroData.images, oldUrl, idx)}
            onRemove={(url) => removeImageFromSection('hero', heroData.images, url)}
          />

          {/* ABOUT SECTION */}
          <section id="about" className="py-16 md:py-24 px-4 md:px-6 bg-gradient-to-r from-rose-50 via-orange-50 to-amber-50 bg-[length:400%_400%] animate-gradient-xy">
            <SectionReveal>
              <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-10 md:gap-16 items-center">
                <div className="relative">
                  <SimpleCarousel
                    images={aboutData.images || []}
                    isAdmin={isAdminMode}
                    onAdd={() => addImageToSection('about_little_andaman', aboutData.images)}
                    onEdit={(oldUrl, idx) => editImageInSection('about_little_andaman', aboutData.images, oldUrl, idx)}
                    onRemove={(url) => removeImageFromSection('about_little_andaman', aboutData.images, url)}
                  />
                </div>
                <div className="space-y-8">
                  <EditableText tag="h3" className="font-bold text-3xl md:text-5xl text-primary leading-tight tracking-tight" text={aboutData.heading} id="heading" isAdmin={isAdminMode} onSave={(_, val) => updateSection('about_little_andaman', { heading: val })} />
                  <EditableText tag="p" className="text-secondary/80 leading-relaxed text-lg font-normal" text={aboutData.text} id="text" isAdmin={isAdminMode} onSave={(_, val) => updateSection('about_little_andaman', { text: val })} />
                  <button className="text-accent hover:underline font-medium text-lg">Read more &rarr;</button>
                </div>
              </div>
            </SectionReveal>
          </section>

          {/* OUR RESORT SECTION */}
          <section id="our-resort" className="py-16 md:py-24 px-4 md:px-6 bg-gradient-to-r from-cyan-50 via-blue-50 to-indigo-50 bg-[length:400%_400%] animate-gradient-xy">
            <SectionReveal>
              <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-10 md:gap-16 items-center">
                <div className="space-y-8 md:order-2">
                  <EditableText tag="h3" className="font-bold text-3xl md:text-5xl text-primary leading-tight tracking-tight" text={resortData.heading} id="heading" isAdmin={isAdminMode} onSave={(_, val) => updateSection('our_resort', { heading: val })} />
                  <EditableText tag="p" className="text-secondary/80 leading-relaxed text-lg font-normal" text={resortData.text} id="text" isAdmin={isAdminMode} onSave={(_, val) => updateSection('our_resort', { text: val })} />
                  <button className="px-8 py-3 bg-primary text-white hover:bg-primary/90 transition-all rounded-full font-medium">Discover Amenities</button>
                </div>
                <div className="relative md:order-1">
                  <SimpleCarousel
                    images={resortData.images || []}
                    isAdmin={isAdminMode}
                    onAdd={() => addImageToSection('our_resort', resortData.images)}
                    onEdit={(oldUrl, idx) => editImageInSection('our_resort', resortData.images, oldUrl, idx)}
                    onRemove={(url) => removeImageFromSection('our_resort', resortData.images, url)}
                  />
                </div>
              </div>
            </SectionReveal>
          </section>

          {/* EXPERIENCES SECTION */}
          <section id="experiences" className="py-16 md:py-24 px-4 md:px-6 bg-gradient-to-r from-emerald-50 via-teal-50 to-green-50 bg-[length:400%_400%] animate-gradient-xy">
            <SectionReveal>
              <div className="max-w-5xl mx-auto space-y-12">
                <div className="text-center space-y-4">
                  <h3 className="font-bold text-3xl md:text-5xl text-primary tracking-tight">Unforgettable Experiences</h3>
                </div>

                <motion.div
                  className="space-y-8"
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true }}
                  variants={{
                    hidden: {},
                    show: {
                      transition: {
                        staggerChildren: 0.2
                      }
                    }
                  }}
                >
                  {experiences.map((item) => (
                    <motion.div key={item.id} variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}>
                      <ExperienceTile
                        item={item}
                        isAdmin={isAdminMode}
                        onUpdate={updateExperience}
                        onDelete={deleteExperience}
                        onAddImage={addExperienceImage}
                        onEditImage={editExperienceImage}
                        onRemoveImage={removeExperienceImage}
                      />
                    </motion.div>
                  ))}
                </motion.div>

                {isAdminMode && (
                  <div className="text-center pt-8">
                    <button onClick={addExperience} className="bg-primary text-white px-8 py-3 rounded-full text-sm font-medium hover:bg-primary/90 transition-colors inline-flex items-center gap-2 shadow-lg">
                      <Plus size={18} /> Add New Experience
                    </button>
                  </div>
                )}
              </div>
            </SectionReveal>
          </section>

          {/* GALLERY GRID */}
          <section id="gallery" className="py-16 md:py-24 px-4 md:px-6 bg-gradient-to-r from-slate-50 via-gray-50 to-zinc-50 bg-[length:400%_400%] animate-gradient-xy">
            <SectionReveal>
              <div className="max-w-7xl mx-auto">
                <div className="flex justify-between items-end mb-16 px-4">
                  <div>
                    <EditableText tag="h2" className="text-3xl md:text-5xl font-bold text-primary mb-4 tracking-tight" text={galleryData.title} id="title" isAdmin={isAdminMode} onSave={(_, val) => updateSection('gallery', { title: val })} />
                    <EditableText tag="p" className="text-secondary/60 text-xl font-normal" text={galleryData.subtitle} id="subtitle" isAdmin={isAdminMode} onSave={(_, val) => updateSection('gallery', { subtitle: val })} />
                  </div>
                  {isAdminMode && (
                    <button onClick={() => addImageToSection('gallery', galleryData.images)} className="bg-primary text-white px-6 py-3 rounded-full text-sm font-medium flex items-center gap-2 hover:bg-primary/90 transition-colors">
                      <Plus size={16} /> Add Photo
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 auto-rows-[200px] md:auto-rows-[300px]">
                  {(galleryData.images || []).filter(x => x).map((url, i) => (
                    <div key={i} onClick={() => setLightboxImage(url)} className={`relative group rounded-2xl overflow-hidden cursor-pointer shadow-sm hover:shadow-xl transition-all duration-500 ${i % 3 === 0 ? 'md:col-span-2 md:row-span-2' : ''}`}>
                      <img src={url} alt={`Gallery image ${i + 1}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                      {isAdminMode && (
                        <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={(e) => { e.stopPropagation(); editImageInSection('gallery', galleryData.images, url, i); }} className="bg-white text-black p-2 rounded-full shadow-lg"><Edit2 size={14} /></button>
                          <button onClick={(e) => { e.stopPropagation(); removeImageFromSection('gallery', galleryData.images, url); }} className="bg-red-500 text-white p-2 rounded-full shadow-lg"><Trash2 size={14} /></button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </SectionReveal>
          </section>
        </main>


        {/* FOOTER */}
        <footer id="contact" className="bg-gradient-to-br from-gray-900 to-gray-800 text-white pt-24 pb-12 px-8">
          <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-16 mb-20">
            <div className="col-span-2 space-y-6">
              <div className="text-3xl font-bold tracking-tight text-white">
                {navData.title}
              </div>
              <EditableText tag="p" className="text-gray-400 max-w-md font-normal leading-relaxed text-lg" text={footerData.aboutText} id="aboutText" isAdmin={isAdminMode} onSave={(_, val) => updateSection('contact_us', { aboutText: val })} />
              <div className="flex gap-4 pt-4">
                <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-white hover:text-black transition-colors cursor-pointer"><Mail size={18} /></div>
                <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-white hover:text-black transition-colors cursor-pointer"><Phone size={18} /></div>
              </div>
            </div>

            <div className="col-span-2 md:col-span-1 space-y-6">
              <EditableText tag="h4" className="text-lg font-semibold text-white tracking-wide" text={footerData.contactTitle} id="contactTitle" isAdmin={isAdminMode} onSave={(_, val) => updateSection('contact_us', { contactTitle: val })} />
              <div className="space-y-4 text-gray-400 text-sm font-normal tracking-wide">
                <div className="flex gap-4 items-start"><MapPin size={18} className="text-white shrink-0 mt-1" /> <EditableText tag="span" text={footerData.address} id="address" isAdmin={isAdminMode} onSave={(_, val) => updateSection('contact_us', { address: val })} /></div>
                <div className="flex gap-4 items-center"><Mail size={18} className="text-white shrink-0" /> <EditableText tag="span" text={footerData.email} id="email" isAdmin={isAdminMode} onSave={(_, val) => updateSection('contact_us', { email: val })} /></div>
                <div className="flex gap-4 items-center"><Phone size={18} className="text-white shrink-0" /> <EditableText tag="span" text={footerData.phone} id="phone" isAdmin={isAdminMode} onSave={(_, val) => updateSection('contact_us', { phone: val })} /></div>
              </div>
            </div>

            <div className="col-span-2 md:col-span-1 space-y-6">
              <h4 className="text-lg font-semibold text-white tracking-wide">Newsletter</h4>
              <p className="text-gray-400 font-normal text-sm">Subscribe to receive exclusive offers and updates.</p>
              <div className="flex border-b border-gray-700 pb-2">
                <input type="email" placeholder="Your email address" className="bg-transparent border-none outline-none text-white placeholder-gray-600 w-full text-sm" />
                <button className="text-white hover:text-gray-300 uppercase text-xs font-bold tracking-widest">Join</button>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-gray-500 uppercase tracking-widest">
            <EditableText tag="p" text={footerData.copyrightText} id="copyrightText" isAdmin={isAdminMode} onSave={(_, val) => updateSection('contact_us', { copyrightText: val })} />
            <button onClick={toggleAdmin} className="flex items-center gap-2 hover:text-white transition-colors">
              {isAdminMode ? <Unlock size={12} /> : <Lock size={12} />} {isAdminMode ? 'Log Out' : 'Admin'}
            </button>
          </div>
        </footer>

      </div>
    </div>
  );
}



const HeroCarousel = ({ data, isAdmin, onSave, onAdd, onEdit, onRemove }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const images = data.images || [];

  useEffect(() => {
    if (images.length === 0) return;
    const timer = setTimeout(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 5000);
    return () => clearTimeout(timer);
  }, [currentIndex, images.length]);

  const goToPrevious = () => {
    if (images.length === 0) return;
    setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
  };

  const goToNext = () => {
    if (images.length === 0) return;
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  return (
    <section className="h-[95vh] w-full relative flex flex-col justify-center items-center text-center px-4 overflow-hidden">
      {/* Image Slides */}
      {images.map((img, index) => (
        <div
          key={index}
          className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ease-in-out ${index === currentIndex ? 'opacity-100' : 'opacity-0'}`}
        >
          <img src={img} alt={`Slide ${index}`} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/20" />
        </div>
      ))}

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60" />

      {/* Admin Controls for Images */}
      {isAdmin && (
        <div className="absolute top-24 right-4 z-20 flex flex-col gap-2">
          <button onClick={onAdd} title="Add New Hero Image" className="bg-white/80 text-black p-2 rounded-full backdrop-blur-md hover:bg-white"><Plus size={16} /></button>
          <button onClick={() => onEdit(images[currentIndex], currentIndex)} title="Edit Current Image" className="bg-white/80 text-black p-2 rounded-full backdrop-blur-md hover:bg-white"><Edit2 size={16} /></button>
          <button onClick={() => onRemove(images[currentIndex])} title="Delete Current Image" className="bg-red-500/80 text-white p-2 rounded-full backdrop-blur-md hover:bg-red-600"><Trash2 size={16} /></button>
        </div>
      )}

      {/* Carousel Navigation */}
      <button onClick={goToPrevious} className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 text-white p-3 rounded-full backdrop-blur-md hover:bg-white/40 transition-colors"><ChevronLeft size={24} /></button>
      <button onClick={goToNext} className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 text-white p-3 rounded-full backdrop-blur-md hover:bg-white/40 transition-colors"><ChevronRight size={24} /></button>

      {/* Text Content */}
      <div className="relative z-10 max-w-5xl mx-auto space-y-8 p-8 md:p-12">
        <div className="space-y-6 animate-in fade-in zoom-in duration-1000">
          <EditableText
            tag="h1"
            className="text-6xl md:text-8xl lg:text-9xl font-bold tracking-tight leading-none text-white drop-shadow-xl"
            text={data.title}
            id="title"
            isAdmin={isAdmin}
            onSave={onSave}
          />
          <EditableText
            tag="p"
            className="text-xl md:text-3xl font-medium text-white/90 max-w-3xl mx-auto leading-relaxed tracking-tight"
            text={data.subtitle}
            id="subtitle"
            isAdmin={isAdmin}
            onSave={onSave}
          />
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.5 }}
          >
            <a href="#our-resort" className="inline-block bg-white text-primary px-10 py-4 rounded-full text-lg font-medium hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transform duration-200">
              <EditableText tag="span" text={data.buttonText} id="buttonText" isAdmin={isAdmin} onSave={onSave} />
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const SimpleCarousel = ({ images, isAdmin, onAdd, onEdit, onRemove }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrevious = () => {
    if (images.length === 0) return;
    setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
  };

  const goToNext = () => {
    if (images.length === 0) return;
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  return (
    <div className="relative group h-[500px] rounded-3xl overflow-hidden bg-gray-200 border border-black/10 shadow-2xl">
      {/* Image Slides */}
      {images.map((img, index) => (
        <div
          key={index}
          className={`absolute inset-0 w-full h-full transition-opacity duration-700 ease-in-out ${index === currentIndex ? 'opacity-100' : 'opacity-0'}`}
        >
          <img src={img} alt={`Slide ${index}`} className="w-full h-full object-cover" />
          {/* Removed dark overlay to keep images bright */}
        </div>
      ))}

      {/* Admin Controls */}
      {isAdmin && (
        <div className="absolute top-3 right-3 z-20 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={onAdd} title="Add New Image" className="bg-black/50 text-white p-2 rounded-full backdrop-blur-md border border-white/20 hover:bg-black/80"><Plus size={14} /></button>
          <button onClick={() => onEdit(images[currentIndex], currentIndex)} title="Edit Current Image" className="bg-black/50 text-white p-2 rounded-full backdrop-blur-md border border-white/20 hover:bg-black/80"><Edit2 size={14} /></button>
          <button onClick={() => onRemove(images[currentIndex])} title="Delete Current Image" className="bg-red-500/80 text-white p-2 rounded-full backdrop-blur-md hover:bg-red-600"><Trash2 size={14} /></button>
        </div>
      )}

      {/* Carousel Navigation */}
      {images.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-3 top-1/2 -translate-y-1/2 z-20 bg-white/50 p-2 rounded-full backdrop-blur-sm hover:bg-white/80 transition-colors opacity-0 group-hover:opacity-100 text-gray-800"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-3 top-1/2 -translate-y-1/2 z-20 bg-white/50 p-2 rounded-full backdrop-blur-sm hover:bg-white/80 transition-colors opacity-0 group-hover:opacity-100 text-gray-800"
          >
            <ChevronRight size={20} />
          </button>
        </>
      )}
    </div>
  );
};

const FastCarousel = ({ images, isAdmin, onAdd, onEdit, onRemove }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (images.length === 0) return;
    const timer = setTimeout(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 2000); // Change slide every 2 seconds
    return () => clearTimeout(timer);
  }, [currentIndex, images.length]);

  return (
    <div className="relative group h-[300px] w-full rounded-2xl overflow-hidden bg-gray-100 border border-gray-200 shadow-sm">
      {/* Image Slides */}
      {images.length > 0 ? (
        images.map((img, index) => (
          <div
            key={index}
            className={`absolute inset-0 w-full h-full transition-opacity duration-700 ease-in-out ${index === currentIndex ? 'opacity-100' : 'opacity-0'}`}
          >
            <img src={img} alt={`Experience ${index}`} className="w-full h-full object-cover" />
          </div>
        ))
      ) : (
        <div className="w-full h-full flex items-center justify-center text-gray-400 font-medium">
          No images yet
        </div>
      )}

      {/* Admin Controls */}
      {isAdmin && (
        <div className="absolute top-2 right-2 z-20 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={onAdd} title="Add Image" className="bg-white text-black p-1.5 rounded-full shadow-lg hover:bg-gray-100 transition-colors"><Plus size={12} /></button>
          {images.length > 0 && (
            <>
              <button onClick={() => onEdit(images[currentIndex], currentIndex)} title="Edit Current" className="bg-white text-black p-1.5 rounded-full shadow-lg hover:bg-gray-100 transition-colors"><Edit2 size={12} /></button>
              <button onClick={() => onRemove(images[currentIndex])} title="Delete Current" className="bg-red-600 text-white p-1.5 rounded-full shadow-lg hover:bg-red-700 transition-colors"><Trash2 size={12} /></button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const ExperienceTile = ({ item, isAdmin, onUpdate, onDelete, onAddImage, onEditImage, onRemoveImage }) => {
  return (
    <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 flex flex-col md:flex-row gap-8 items-center">
      {/* Left: Image Carousel */}
      <div className="w-full md:w-1/3 shrink-0">
        <FastCarousel
          images={item.images || []}
          isAdmin={isAdmin}
          onAdd={() => onAddImage(item.id)}
          onEdit={(url, idx) => onEditImage(item.id, url, idx)}
          onRemove={(url) => onRemoveImage(item.id, url)}
        />
      </div>

      {/* Right: Text Content */}
      <div className="w-full md:w-2/3 space-y-4">
        <div className="flex justify-between items-start">
          <div className="space-y-2 w-full">
            <EditableText
              tag="h4"
              className="text-2xl font-bold text-primary tracking-tight"
              text={item.title}
              id={`title-${item.id}`}
              isAdmin={isAdmin}
              onSave={(_, val) => onUpdate(item.id, { ...item, title: val })}
            />
          </div>
          {isAdmin && (
            <button onClick={() => onDelete(item.id)} className="text-red-400 hover:text-red-600 p-2 -mr-2">
              <Trash2 size={18} />
            </button>
          )}
        </div>

        <EditableText
          tag="p"
          className="text-secondary/80 leading-relaxed font-normal text-lg"
          text={item.description}
          id={`desc-${item.id}`}
          isAdmin={isAdmin}
          onSave={(_, val) => onUpdate(item.id, { ...item, description: val })}
        />
      </div>
    </div>
  );
};